import { PromotionDto } from '@formrails/shared/data-access';
import { Injectable, inject, ChangeDetectorRef, NgZone } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { take, filter } from 'rxjs/operators';
import { Form, FormStatus, QuestionView, generateGuid, ApplicationTemplateDto } from '../models';
import { FormPersistenceService } from './form-persistence.service';
import { FormStore } from '../store/form.store';

export interface FormLoadContext {
  formId: string;
  savedTemplateId: string | null;
  currentVersion: number;
}

export interface FormLoadResult {
  formId: string;
  savedTemplateId: string | null;
  currentVersion: number;
}

export interface FormLoadCallbacks {
  onFormIdChange: (formId: string) => void;
  onSavedTemplateIdChange: (id: string | null) => void;
  onCurrentVersionChange: (version: number) => void;
  onFormTitleChange: (title: string) => void;
  onFormDescriptionChange: (description: string) => void;
  onFormVersionChange: (version: number) => void;
  onFormStatusChange: (status: FormStatus) => void;
  onShowQuestionNumbersChange: (show: boolean) => void;
  onQuestionsChange: (questions: QuestionView[]) => void;
  /** Application templates only; see AssessmentMatricesComponent. */
  onAssessmentMatrixIdsChange?: (ids: string[]) => void;
  /** Promotion state of the loaded version, null when never asked for. */
  onPromotionChange?: (promotion: PromotionDto | null) => void;
  /**
   * The template a clone was taken from, sent with the first save. Null for a
   * plain load, which is not a clone of anything.
   */
  onClonedFromChange?: (templateId: string | null) => void;
  onReadOnlyChange: (readOnly: boolean) => void;
  /**
   * Fired when a load or clone has fully populated local + store state.
   * `source` distinguishes a pristine load (safe to treat as unchanged) from
   * a clone (new unsaved content).
   */
  onLoadComplete?: (source: 'load' | 'clone') => void;
}

/**
 * Service to handle form loading and cloning operations
 */
@Injectable({
  providedIn: 'root'
})
export class FormLoadService {
  private formStore = inject(FormStore);
  private formPersistenceService = inject(FormPersistenceService);
  /** Observable view of the store's current form, used to await async API loads. */
  private readonly currentForm$ = toObservable(this.formStore.currentForm);

  /**
   * Handle form selection from the form selector modal
   */
  onFormSelected(
    templateDto: ApplicationTemplateDto,
    callbacks: FormLoadCallbacks,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    callbacks.onReadOnlyChange(false);
    this.formStore.setReadOnly(false);

    window.scrollTo({ top: 0, behavior: 'instant' });
    const scrollableArea = document.querySelector('.questions-scrollable-area') as HTMLElement;
    if (scrollableArea) {
      scrollableArea.scrollTop = 0;
    }

    callbacks.onSavedTemplateIdChange(templateDto.id);
    this.formStore.getRawFormByTemplateId(templateDto.id);

    this.currentForm$.pipe(
      filter(form => form !== null && form.id === templateDto.id),
      take(1)
    ).subscribe(form => {
      if (form) {
        this.loadForm(form, false, callbacks, ngZone, cdr);
        // Record which backend owns this template so saves route correctly.
        this.formStore.updateFormType(templateDto.formTemplateType ?? 'Application');
      }
    });
  }

  /**
   * Handle form cloning from the form selector modal
   */
  onFormCloned(
    templateDto: ApplicationTemplateDto,
    callbacks: FormLoadCallbacks,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    callbacks.onReadOnlyChange(false);
    this.formStore.setReadOnly(false);
    this.formStore.getRawFormByTemplateId(templateDto.id);

    this.currentForm$.pipe(
      filter(form => form !== null && form.id === templateDto.id),
      take(1)
    ).subscribe(form => {
      if (form) {
        this.cloneForm(form, callbacks, ngZone, cdr);
        // A clone inherits the source template's backend.
        this.formStore.updateFormType(templateDto.formTemplateType ?? 'Application');
      }
    });
  }

  /**
   * Load a form into the builder
   */
  loadForm(
    form: Form,
    readOnly: boolean,
    callbacks: FormLoadCallbacks,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    callbacks.onReadOnlyChange(readOnly);
    this.formStore.setReadOnly(readOnly);
    this.formPersistenceService.clearForm();

    callbacks.onFormIdChange(form.id);
    // Version is user-managed: load it as-is and let the author decide when
    // (and by how much) to bump it via the header's version field.
    const loadedVersion = form.version ?? 0;
    callbacks.onCurrentVersionChange(loadedVersion);

    // Update local state immediately
    callbacks.onFormTitleChange(form.title);
    callbacks.onFormDescriptionChange(form.description || '');
    callbacks.onFormVersionChange(loadedVersion);
    callbacks.onFormStatusChange(form.status);
    callbacks.onShowQuestionNumbersChange(form.showQuestionNumbers);
    callbacks.onAssessmentMatrixIdsChange?.(form.assessmentMatrixIds ?? []);
    callbacks.onPromotionChange?.(form.promotion ?? null);
    // A saved template carries nothing; a restored draft of a clone carries the
    // source it was taken from, so this both restores and clears.
    this.formStore.updateClonedFrom(form.clonedFromId ?? null);
    callbacks.onClonedFromChange?.(form.clonedFromId ?? null);

    const questionsToAdd: QuestionView[] = (form.questions || []).map(question => ({
      ...question,
      showMenu: question.showMenu ?? false,
      isEditing: readOnly ? false : (question.isEditing ?? false),
      order: question.order ?? 0
    }));

    callbacks.onQuestionsChange([...questionsToAdd]);

    ngZone.run(() => {
      cdr.markForCheck();
      cdr.detectChanges();
    });

    this.loadFormIntoStore(form, readOnly, questionsToAdd, callbacks, ngZone, cdr);
  }

  private loadFormIntoStore(
    form: Form,
    readOnly: boolean,
    questionsToAdd: QuestionView[],
    callbacks: FormLoadCallbacks,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    // patchState is synchronous, so the store can be read immediately after each call —
    // the former "dispatch then wait for the store to settle" choreography is unnecessary.
    if (this.formStore.questions().length > 0) {
      this.formStore.reorderQuestions([]);
    }

    const versionToUse = form.version ?? 0;
    this.formStore.updateFormTitle(form.title);
    this.formStore.updateFormDescription(form.description || '');
    this.formStore.updateFormVersion(versionToUse);
    this.formStore.updateFormStatus(form.status);
    this.formStore.toggleShowQuestionNumbers(form.showQuestionNumbers);
    if (form.formType) {
      // Restored drafts carry their backend ownership through localStorage.
      this.formStore.updateFormType(form.formType);
    }

    if (questionsToAdd.length > 0) {
      this.formStore.addQuestions(questionsToAdd);

      const storeQuestions = this.formStore.questions();
      callbacks.onQuestionsChange([...storeQuestions]);

      ngZone.run(() => {
        cdr.markForCheck();
        cdr.detectChanges();

        requestAnimationFrame(() => {
          const scrollableArea = document.querySelector('.questions-scrollable-area') as HTMLElement;
          if (scrollableArea) scrollableArea.scrollTop = 0;
          window.scrollTo({ top: 0, behavior: 'instant' });
          cdr.markForCheck();
          cdr.detectChanges();
          this.formPersistenceService.saveForm();
        });
      });
    } else {
      ngZone.run(() => {
        cdr.markForCheck();
        cdr.detectChanges();
        this.formPersistenceService.saveForm();
      });
    }

    callbacks.onLoadComplete?.('load');
  }

  /**
   * Clone a form with a new ID
   */
  cloneForm(
    form: Form,
    callbacks: FormLoadCallbacks,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    this.formPersistenceService.clearForm();

    const newFormId = generateGuid();
    callbacks.onFormIdChange(newFormId);
    callbacks.onSavedTemplateIdChange(null);
    callbacks.onCurrentVersionChange(0);

    // patchState is synchronous — read the store directly after each mutation.
    if (this.formStore.questions().length > 0) {
      this.formStore.reorderQuestions([]);
    }

    callbacks.onQuestionsChange([]);
    callbacks.onFormTitleChange(`${form.title} (Copy)`);
    callbacks.onFormDescriptionChange(form.description || '');
    callbacks.onFormVersionChange(1);
    callbacks.onFormStatusChange(FormStatus.Draft);
    callbacks.onShowQuestionNumbersChange(form.showQuestionNumbers);

    this.formStore.updateFormTitle(`${form.title} (Copy)`);
    this.formStore.updateFormDescription(form.description || '');
    this.formStore.updateFormVersion(1);
    this.formStore.updateFormStatus(FormStatus.Draft);
    this.formStore.toggleShowQuestionNumbers(form.showQuestionNumbers);

    callbacks.onAssessmentMatrixIdsChange?.(form.assessmentMatrixIds ?? []);
    // A clone is a new version: it has never been promoted.
    callbacks.onPromotionChange?.(null);
    // Remembered until the first save, which is what records the provenance.
    // Kept on the store so it is written to the draft and survives a refresh.
    this.formStore.updateClonedFrom(form.id);
    callbacks.onClonedFromChange?.(form.id);

    const clonedQuestions: QuestionView[] = form.questions.map(question => ({
      ...question,
      order: question.order || 0,
      showMenu: false,
      isEditing: false
    }));

    callbacks.onQuestionsChange([...clonedQuestions]);

    ngZone.run(() => {
      cdr.markForCheck();
      cdr.detectChanges();
    });

    this.formStore.addQuestions(clonedQuestions);

    const storeQuestions = this.formStore.questions();
    callbacks.onQuestionsChange([...storeQuestions]);

    ngZone.run(() => {
      cdr.markForCheck();
      cdr.detectChanges();
    });

    setTimeout(() => {
      this.formPersistenceService.saveForm();
    }, 100);

    callbacks.onLoadComplete?.('clone');
  }
}

